import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  ButtonGroup,
  Card,
  Spinner,
  Table,
} from "react-bootstrap";
import { useGetLazy } from "../../api";
import { EmptyState } from "../../components/ui";
import { ICouponAnalyticsEventsResponse } from "../../interfaces";
import { ICouponQuery, queryParams } from "./coupon-query";
import { BrandLink, CouponSearchLink, DealLink } from "./kindred-links";
import { ReviewCell, TCouponReviews } from "./coupon-reviews";

const PAGE_SIZE = 100;

const short = (id: string | null) => (id ? id.slice(0, 8) : "—");

// Pinned right so it stays in view while the wide table scrolls.
const REVIEW_COL: React.CSSProperties = {
  position: "sticky",
  right: 0,
  background: "var(--bs-body-bg)",
  boxShadow: "inset 1px 0 0 var(--bs-border-color)",
};

/** The rows exactly as the extension sent them, filtered by the page's filter bar. */
export const CouponRawEvents: React.FC<{
  query: ICouponQuery | null;
  active: boolean;
  onCheckout: (eventId: string) => void;
  reviews: TCouponReviews;
}> = ({ query, active, onCheckout, reviews }) => {
  const { execute, data, loading, error } =
    useGetLazy<ICouponAnalyticsEventsResponse>();
  const [page, setPage] = useState(0);
  const fetched = useRef<{ query: ICouponQuery; page: number } | null>(null);

  // A new Search starts at page 0; an auto-refresh keeps the page.
  const [seen, setSeen] = useState(query);
  if (seen !== query) {
    setSeen(query);
    if (!query?.refreshed) setPage(0);
  }

  // A refresh keeps the page, which may now be past the end.
  useEffect(() => {
    if (!data || data.items.length || !data.total || page === 0) return;
    setPage(Math.max(0, Math.ceil(data.total / PAGE_SIZE) - 1));
  }, [data, page]);

  useEffect(() => {
    if (!active || !query) return;
    const last = fetched.current;
    if (last && last.query === query && last.page === page) return;
    fetched.current = { query, page };
    const p = queryParams(query, "raw");
    p.set("offset", String(page * PAGE_SIZE));
    p.set("limit", String(PAGE_SIZE));
    execute(`/coupon-performance/events?${p.toString()}`).catch(() => null);
  }, [active, query, page, execute]);

  if (!query)
    return (
      <EmptyState
        icon="bi-table"
        title="Nothing loaded yet"
        message="Set the filters above, then press Search."
      />
    );

  const total = data?.total ?? 0;
  const start = total === 0 ? 0 : page * PAGE_SIZE + 1;
  const end = Math.min(total, (page + 1) * PAGE_SIZE);

  return (
    <>
      {error && (
        <Alert variant="danger" className="small">
          Could not load the raw rows: {error.message}
        </Alert>
      )}
      <Card>
        <Card.Body className="p-0">
          {loading && !data ? (
            <div className="text-center py-4">
              <Spinner />
            </div>
          ) : (
            <Table
              hover
              responsive
              size="sm"
              className="mb-0 small align-middle"
            >
              <thead>
                <tr>
                  <th>Time</th>
                  <th title="Click to see every code tried in that checkout">
                    Checkout
                  </th>
                  <th>Partner</th>
                  <th>Platform</th>
                  <th title="The shopper's country, not the merchant's">
                    Country
                  </th>
                  <th>Brand</th>
                  <th title="The deal the code ran under, first four characters. Hover for the full id.">
                    Deal
                  </th>
                  <th>Code</th>
                  <th>Result</th>
                  <th title="The code the extension kept for the checkout">
                    Kept
                  </th>
                  <th className="text-end">Saved</th>
                  <th className="text-end">Basket</th>
                  <th>Message</th>
                  <th>URL</th>
                  <th
                    style={REVIEW_COL}
                    title="Claim the brand in this shopper country and put it on the Work Pipeline; then the bucket it is in and who has it"
                  >
                    Review
                  </th>
                </tr>
              </thead>
              <tbody>
                {(data?.items ?? []).map((r) => (
                  <tr key={r.id}>
                    <td className="text-nowrap">
                      {new Date(r.timeStamp).toLocaleString()}
                    </td>
                    <td>
                      <Button
                        size="sm"
                        variant="link"
                        className="p-0 font-monospace small"
                        onClick={() => onCheckout(r.eventId)}
                        title={`Show every code tried in checkout ${r.eventId}`}
                      >
                        {short(r.eventId)}
                      </Button>
                    </td>
                    <td title={r.partnerId}>
                      {r.partnerName ?? short(r.partnerId)}
                    </td>
                    <td>{r.platform}</td>
                    <td>{r.country ?? "—"}</td>
                    <td
                      className="font-monospace"
                      title={`Brand ${r.brandId ?? "—"}\nDeal ${r.campaignId ?? "—"}`}
                    >
                      {short(r.brandId)}
                      <BrandLink brandId={r.brandId} />
                    </td>
                    <td>
                      <DealLink campaignId={r.campaignId} />
                    </td>
                    <td className="text-nowrap">
                      <span className="font-monospace">{r.code ?? "—"}</span>
                      <CouponSearchLink
                        code={r.code}
                        brandId={r.brandId}
                        country={r.country}
                      />
                    </td>
                    <td>
                      <Badge bg={r.status === "Applied" ? "success" : "danger"}>
                        {r.status}
                      </Badge>
                    </td>
                    <td>{r.isCheckoutCoupon ? "Yes" : ""}</td>
                    <td className="text-end">
                      {r.status === "Applied"
                        ? `${r.currency ?? ""}${r.amountSaved}`
                        : ""}
                    </td>
                    <td className="text-end">
                      {r.basketValue
                        ? `${r.currency ?? ""}${r.basketValue}`
                        : "—"}
                    </td>
                    <td
                      className="text-muted text-truncate"
                      style={{ maxWidth: 280 }}
                      title={r.errorMessage ?? ""}
                    >
                      {r.errorMessage ?? ""}
                    </td>
                    <td>
                      {r.dealUrl ? (
                        <a
                          href={r.dealUrl}
                          target="_blank"
                          rel="noreferrer"
                          title={r.dealUrl}
                        >
                          <i className="bi bi-box-arrow-up-right" />
                        </a>
                      ) : (
                        ""
                      )}
                    </td>
                    <td style={REVIEW_COL} className="text-nowrap">
                      <ReviewCell row={r} reviews={reviews} />
                    </td>
                  </tr>
                ))}
                {data && !data.items.length && (
                  <tr>
                    <td colSpan={15} className="text-center text-muted py-4">
                      No rows match these filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
        <div className="d-flex align-items-center justify-content-between px-3 py-2 border-top">
          <small className="text-muted">
            {loading && data && (
              <Spinner size="sm" className="me-1" title="Refreshing" />
            )}
            {start}–{end} of {total.toLocaleString()} rows, newest first
          </small>
          <ButtonGroup size="sm">
            <Button
              variant="outline-secondary"
              disabled={page === 0 || loading}
              onClick={() => setPage(page - 1)}
              title="Previous page"
            >
              Previous
            </Button>
            <Button
              variant="outline-secondary"
              disabled={end >= total || loading}
              onClick={() => setPage(page + 1)}
              title="Next page"
            >
              Next
            </Button>
          </ButtonGroup>
        </div>
      </Card>
    </>
  );
};
